import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  ReactNode,
} from 'react'
import { getMe } from '../api/auth'

interface User {
  id: string
  fullName: string
  email: string
  phone?: string
  role: string
  createdAt: string
  [key: string]: any
}

interface AuthState {
  user: User | null
  token: string | null
  isAuthenticated: boolean
  isLoading: boolean
}

type AuthAction =
  | { type: 'LOGIN'; payload: { token: string; user: User } }
  | { type: 'LOGOUT' }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'UPDATE_USER'; payload: Partial<User> }

const initialState: AuthState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,
}

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'LOGIN':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
      }
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      }
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      }
    case 'UPDATE_USER':
      return {
        ...state,
        user: state.user
          ? { ...state.user, ...action.payload }
          : action.payload,
      }
    default:
      return state
  }
}

interface AuthContextType extends AuthState {
  login: (token: string, user: User) => void
  logout: () => void
  setLoading: (isLoading: boolean) => void
  updateUser: (data: Partial<User>) => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(authReducer, initialState)

  useEffect(() => {
    const initializeAuth = async () => {
      const storedToken = localStorage.getItem('bella_token')
      if (storedToken) {
        try {
          const userData = await getMe(storedToken)
          dispatch({
            type: 'LOGIN',
            payload: { token: storedToken, user: userData },
          })
        } catch (error) {
          // console.error('Failed to restore session:', error)
          localStorage.removeItem('bella_token')
          dispatch({ type: 'LOGOUT' })
        }
      } else {
        dispatch({ type: 'LOGOUT' })
      }
    }

    initializeAuth()
  }, [])

  const login = (token: string, user: User) => {
    localStorage.setItem('bella_token', token)
    dispatch({ type: 'LOGIN', payload: { token, user } })
  }

  const logout = () => {
    localStorage.removeItem('bella_token')
    dispatch({ type: 'LOGOUT' })
  }

  const setLoading = (isLoading: boolean) => {
    dispatch({ type: 'SET_LOADING', payload: isLoading })
  }

  const updateUser = (data: Partial<User>) => {
    dispatch({ type: 'UPDATE_USER', payload: data })
  }

  return React.createElement(
    AuthContext.Provider,
    { value: { ...state, login, logout, setLoading, updateUser } },
    children,
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
